/**
 * Reachability (R29/D105) — "you can see this and not reach it", asked in one
 * place by everyone who needs the answer.
 *
 * @remarks
 * Extracted from `execute-turn.ts` when Calder's D105 wiring exposed §4.1:
 * the reach gate ran only at the executeTurn interception, so a NARROWING
 * refusal fired first — `open desk` through the glass answered a question
 * about instruments ("nothing here would open it") when the player's actual
 * obstacle was that they could not reach it, inviting exactly the tool hunt
 * the glass makes pointless. The rule his roster demonstrated is the rule
 * everywhere now: **the nearest barrier speaks first** — reachability is
 * nearer than instrument availability, portability, or a missing door,
 * because you cannot even attempt the thing.
 *
 * @packageDocumentation
 */

import { containerItemIdOf, type ItemDefinition, type ItemId, type RuntimeState } from '@orpheus/content-schema';

import type { ActionRejectionReason } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { nameInSentence, speakSlot } from '../defaults/default-responses.js';
import { isShutSeeThrough } from '../entities/item-state.js';
import { type SpeakableLine } from './action-handler-result.js';

/** What stands between the hands and a thing: an unmet `reachableWhen` on it or above it, or (D253) a shut see-through door. */
type Barrier = { readonly containerItemId: ItemId; readonly type: 'shut_door' } | { readonly type: 'reach_unmet' };

/**
 * The first barrier between the hands and this item, walking up from it —
 * its own `reachableWhen`, then each container's door and `reachableWhen` in
 * turn (the drawer behind the glass is barred by the glass) — and none when
 * the player already carries it (a thing in hand is reached by definition).
 *
 * @remarks
 * D253: a shut door of mesh or glass is a barrier of its own kind. The
 * catalog lets what is behind it be named, so this walk is what keeps it out
 * of the hands, and the refusal can say which door is shut.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param itemId - The item to test
 * @returns The barrier, or `undefined` when the item is within reach
 */
const barrierOf = (bundle: EngineGameBundle, state: RuntimeState, itemId: ItemId): Barrier | undefined => {
	if (state.world.inventoryItemIds.includes(itemId)) {
		return undefined;
	}

	// The walk is cycle-safe by construction — the D50 containment validator
	// refuses cyclic packs — but the guard keeps a hand-built test bundle from
	// hanging the suite.
	const walkedItemIds = new Set<ItemId>();
	let currentItemId: ItemId | undefined = itemId;

	while (currentItemId !== undefined && !walkedItemIds.has(currentItemId)) {
		walkedItemIds.add(currentItemId);

		const currentItem: ItemDefinition = requireItem(bundle, currentItemId);

		if (currentItem.reachableWhen !== undefined && !evaluateCondition(currentItem.reachableWhen, bundle, state)) {
			return { type: 'reach_unmet' };
		}

		// D240: a body's pockets end the walk — a body within reach lends its reach to what it carries.
		currentItemId = containerItemIdOf(currentItem.containedIn);

		if (currentItemId !== undefined && isShutSeeThrough(requireItem(bundle, currentItemId), state)) {
			return { containerItemId: currentItemId, type: 'shut_door' };
		}
	}

	return undefined;
};

/**
 * Is this item barred right now — seen, and not to be reached ({@link barrierOf})?
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param itemId - The item to test
 * @returns Whether the item is out of reach
 */
export const isItemUnreachable = (bundle: EngineGameBundle, state: RuntimeState, itemId: ItemId): boolean =>
	barrierOf(bundle, state, itemId) !== undefined;

/**
 * The one refusal an out-of-reach item earns, wherever it is refused: the
 * typed reason plus the item's authored `unreachableNarratives` (or the
 * `outOfReach` slot) — so the executeTurn gate and the narrowing sites speak
 * with one voice.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param itemId - The barred item
 * @returns The reason and the lines, for whichever `rejected` builds the arm
 */
export const unreachableRefusalParts = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	itemId: ItemId,
): { readonly reason: ActionRejectionReason; readonly speakable: readonly SpeakableLine[] } => {
	const barredItem = requireItem(bundle, itemId);
	const barrier = barrierOf(bundle, state, itemId);

	return {
		reason: { itemId, type: 'unreachable' },
		speakable: barredItem.unreachableNarratives ?? [
			barrier?.type === 'shut_door'
				? speakSlot(bundle, state, 'seenBehindShutDoor', {
					containerName: nameInSentence(bundle, requireItem(bundle, barrier.containerItemId).name),
					itemName: barredItem.name,
				})
				: speakSlot(bundle, state, 'outOfReach', { itemName: barredItem.name }),
		],
	};
};
